/**
 * Display formatting. SPEC.md §19, §8.6, §16.5.
 *
 * Scores always carry an explicit sign so the value never depends on colour
 * alone to communicate direction. §19
 */

import { PLAYER_NAME_MAX_LENGTH } from './constants.js';

/** "+12", "-7", "0". Never "+0" or "-0". §19 */
export function formatSigned(value: number): string {
  if (value === 0) return '0';
  return value > 0 ? `+${value}` : `${value}`;
}

/** Screen-reader wording for a score, since the glyph alone is ambiguous. §19 */
export function describeScore(value: number): string {
  if (value === 0) return 'level, 0 points';
  return value > 0 ? `up ${value} points` : `down ${Math.abs(value)} points`;
}

/** "+38 / -38 ✓" — the compact centre balance readout. §14.1 */
export function formatBalanceCompact(positive: number, negative: number): string {
  const balanced = positive + negative === 0;
  return `${formatSigned(positive)} / ${formatSigned(negative)} ${balanced ? '✓' : '⚠'}`;
}

/** Truncates for the ring; full names stay available in details. §8.6 */
export function truncateName(name: string, max: number = PLAYER_NAME_MAX_LENGTH): string {
  const trimmed = name.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, Math.max(1, max - 1))}…`;
}

/** "Offline 43s", "Offline 4m". §16.5 */
export function formatOfflineFor(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `Offline ${s}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `Offline ${minutes}m`;
  return `Offline ${Math.floor(minutes / 60)}h`;
}

/** "19:42" in the viewer's locale, for history rows. §13.1 */
export function formatClockTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Hand label used throughout the UI. §7.3 */
export function formatHandLabel(roundNumber: number): string {
  return `HAND ${roundNumber}`;
}
